import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router';
import { useState } from 'react';
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Popover,
  PopoverButton,
  PopoverPanel,
} from '@headlessui/react';
import { ButtonType } from '@src/types/types';
import { Button } from '@components/atoms/Button';
import { classNames } from '@utils/classNames';
import { Icon } from '@components/atoms/Icon';
import { IconType } from '@src/types/IconType';
import { ExternalLinks, Paths } from '@src/routes/paths';
import { Environment } from '@src/environment';
import { Logo } from '@components/atoms/Logo';
import { useImpersonate } from '@contexts/ImpersonateContext.tsx';

const NavItem = ({
  icon,
  label,
  to,
  external,
  iconClassName,
  onClick,
}: {
  icon: IconType;
  label: string;
  to: string;
  external?: boolean;
  iconClassName?: string;
  onClick?: () => void;
}) => {
  const content = (
    <span className="flex items-center gap-[5px]">
      <Icon
        type={icon}
        className={classNames('w-[18px] h-[18px]', iconClassName)}
      />
      <span className="font-bold text-base text-dark">{label}</span>
    </span>
  );

  if (external) {
    return (
      <a href={to} target="_blank" rel="noopener noreferrer" onClick={onClick}>
        {content}
      </a>
    );
  }

  return (
    <a href={to} onClick={onClick}>
      {content}
    </a>
  );
};

const SupportContent = () => {
  const { t } = useTranslation('common');
  const showPhone = t('nav.showPhone') === 'true';

  return (
    <div className="flex flex-col gap-3 bg-grey3light rounded-bonusLarge p-2.5">
      <a
        href={t('nav.faqUrl')}
        className="flex items-center gap-[5px] whitespace-nowrap"
      >
        <Icon
          type={IconType.Question}
          className="w-3 h-3 shrink-0 text-grey1"
        />
        <span className="font-medium text-xs text-dark">{t('nav.faq')}</span>
      </a>
      <a
        href={`mailto:${t('nav.supportEmail')}`}
        className="flex items-center gap-[5px]"
      >
        <Icon type={IconType.Envelope} className="w-3 h-3 text-grey1" />
        <span className="font-medium text-xs text-dark">
          {t('nav.supportEmail')}
        </span>
      </a>
      {showPhone && (
        <a
          href={`tel:${t('nav.supportPhone')}`}
          className="flex items-center gap-[5px] whitespace-nowrap"
        >
          <Icon type={IconType.Phone} className="w-3 h-3 shrink-0 text-grey1" />
          <span className="font-medium text-xs text-dark">
            <div>{t('nav.supportPhone')}</div>
            <div>{'\n' + t('nav.supportPhoneInfo')}</div>
          </span>
        </a>
      )}
    </div>
  );
};

const AccountContent = ({ onAction }: { onAction?: () => void }) => {
  const { t } = useTranslation('common');
  const auth = useAuth();

  return (
    <div className="flex flex-col gap-4 bg-grey3light rounded-bonusLarge p-2.5 whitespace-nowrap">
      <a
        href={ExternalLinks.EDIT_PROFILE_PAGE}
        className="flex items-center gap-[5px]"
        onClick={onAction}
      >
        <Icon type={IconType.ProfileUser} className="w-3 h-3 text-grey1" />
        <span className="font-medium text-xs text-dark">
          {t('nav.myProfile')}
        </span>
      </a>
      <a
        href={t('nav.myOrdersUrl')}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-[5px]"
        onClick={onAction}
      >
        <Icon type={IconType.Orders} className="w-3 h-3 text-grey1" />
        <span className="font-medium text-xs text-dark">
          {t('nav.myOrders')}
        </span>
      </a>
      <div className="border-t border-grey2 pt-4">
        <button
          className="flex items-center gap-[5px] w-full"
          onClick={() => {
            onAction?.();
            auth.signoutRedirect({
              post_logout_redirect_uri: `${Environment.PUBLIC_URL}${Paths.PROFILE}`,
            });
          }}
        >
          <Icon type={IconType.Logout} className="w-3 h-3 text-red" />
          <span className="font-medium text-xs text-red">
            {t('nav.logOut')}
          </span>
        </button>
      </div>
    </div>
  );
};

const DesktopNav = () => {
  const { t } = useTranslation('common');

  return (
    <nav className="hidden md:flex items-center w-full pl-10">
      <div className="flex items-center gap-7">
        <NavItem
          icon={IconType.Overview}
          label={t('nav.overview')}
          to={Paths.PROFILE}
          iconClassName="text-ivarioBlue"
        />
        <NavItem
          icon={IconType.Shop}
          label={t('nav.onlineshop')}
          to={t('nav.onlineshopUrl')}
          external
          iconClassName="text-ivarioBlue"
        />
        <Popover className="relative">
          {({ open }) => (
            <>
              <PopoverButton className="flex items-center gap-[5px] outline-none">
                <Icon
                  type={IconType.Support}
                  className="w-[18px] h-[18px] text-ivarioBlue"
                />
                <span className="font-bold text-base text-dark">
                  {t('nav.support')}
                </span>
                <Icon
                  type={IconType.ChevronDown}
                  className={classNames(
                    'w-3 h-3 text-dark transition-transform duration-200',
                    open ? 'rotate-180' : '',
                  )}
                />
              </PopoverButton>
              <PopoverPanel className="absolute left-0 top-full mt-2 z-50">
                <SupportContent />
              </PopoverPanel>
            </>
          )}
        </Popover>
      </div>
      <div className="ml-auto">
        <Popover className="relative">
          {({ open }) => (
            <>
              <PopoverButton className="flex items-center gap-[5px] outline-none">
                <Icon
                  type={IconType.User}
                  className="w-[18px] h-[18px] text-ivarioBlue"
                />
                <span className="font-bold text-base text-dark">
                  {t('nav.myAccount')}
                </span>
                <Icon
                  type={IconType.ChevronDown}
                  className={classNames(
                    'w-3 h-3 text-dark transition-transform duration-200',
                    open ? 'rotate-180' : '',
                  )}
                />
              </PopoverButton>
              <PopoverPanel className="absolute right-0 top-full mt-2 z-50">
                <AccountContent />
              </PopoverPanel>
            </>
          )}
        </Popover>
      </div>
    </nav>
  );
};

const MobileNav = () => {
  const { t } = useTranslation('common');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="md:hidden">
      <button
        onClick={() => setIsMenuOpen((open) => !open)}
        className={classNames('p-2 rounded-bonusLarge')}
      >
        <Icon
          type={isMenuOpen ? IconType.Cross : IconType.BurgerMenu}
          className="w-6 h-6"
        />
      </button>
      {isMenuOpen && (
        <div className="absolute top-full left-0 right-0 bg-background shadow-[0_4px_4px_rgba(0,0,0,0.1)] px-5 py-5 z-50">
          <div className="flex flex-col gap-7">
            <NavItem
              icon={IconType.Overview}
              label={t('nav.overview')}
              to={Paths.PROFILE}
              iconClassName="text-ivarioBlue"
              onClick={() => setIsMenuOpen(false)}
            />
            <NavItem
              icon={IconType.Shop}
              label={t('nav.onlineshop')}
              to={t('nav.onlineshopUrl')}
              external
              iconClassName="text-ivarioBlue"
              onClick={() => setIsMenuOpen(false)}
            />
            <NavItem
              icon={IconType.Question}
              label={t('nav.faq')}
              to={t('nav.faqUrl')}
              external
              iconClassName="text-ivarioBlue"
              onClick={() => setIsMenuOpen(false)}
            />
            <Disclosure>
              {({ open }) => (
                <div className="flex flex-col gap-2">
                  <DisclosureButton className="flex items-center justify-between w-full">
                    <span className="flex items-center gap-[5px]">
                      <Icon
                        type={IconType.Support}
                        className="w-[18px] h-[18px] text-ivarioBlue"
                      />
                      <span className="font-bold text-base text-dark">
                        {t('nav.support')}
                      </span>
                    </span>
                    <Icon
                      type={IconType.ChevronDown}
                      className={classNames(
                        'w-3 h-3 text-dark transition-transform duration-200',
                        open ? 'rotate-180' : '',
                      )}
                    />
                  </DisclosureButton>
                  <DisclosurePanel>
                    <SupportContent />
                  </DisclosurePanel>
                </div>
              )}
            </Disclosure>
            <Disclosure>
              {({ open }) => (
                <div className="flex flex-col gap-2">
                  <DisclosureButton className="flex items-center justify-between w-full">
                    <span className="flex items-center gap-[5px]">
                      <Icon
                        type={IconType.User}
                        className="w-[18px] h-[18px] text-ivarioBlue"
                      />
                      <span className="font-bold text-base text-dark">
                        {t('nav.myAccount')}
                      </span>
                    </span>
                    <Icon
                      type={IconType.ChevronDown}
                      className={classNames(
                        'w-3 h-3 text-dark transition-transform duration-200',
                        open ? 'rotate-180' : '',
                      )}
                    />
                  </DisclosureButton>
                  <DisclosurePanel>
                    <AccountContent onAction={() => setIsMenuOpen(false)} />
                  </DisclosurePanel>
                </div>
              )}
            </Disclosure>
          </div>
        </div>
      )}
    </div>
  );
};

export const Header = ({ parentPage }: { parentPage?: Paths }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const auth = useAuth();
  const { isImpersonating, impersonateSampleId } = useImpersonate();

  return (
    <>
      <div className="relative flex items-center justify-between py-[15px] w-screen -ml-4 px-5 shadow-[0_4px_4px_0_rgba(0,0,0,0.1)] md:py-8 md:w-full md:ml-0 md:px-0 md:shadow-none">
        <Logo className="ml-1 md:ml-0" />
        {auth.isAuthenticated && (
          <>
            <DesktopNav />
            <MobileNav />
          </>
        )}
      </div>
      {parentPage && (
        <div className="mb-6">
          <Button
            buttonType={ButtonType.TERTIARY}
            IconLeading={IconType.ArrowLeft}
            onClick={() =>
              navigate(
                parentPage +
                  (isImpersonating
                    ? `?impersonate=${impersonateSampleId}`
                    : ''),
              )
            }
            label={t('back')}
            className="w-fit px-5"
          />
        </div>
      )}
    </>
  );
};
